import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Input = ({ 
  label, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  error, 
  required = false,
  disabled = false,
  className = '',
  icon,
  responsive = false,
  size = 'md',
  ...props 
}) => {
  const { isMobile, getResponsiveValue, getComponentSize } = useTheme();

  // Get responsive size configuration
  const responsiveSize = responsive ? getComponentSize('input', size) : null;

  // Default responsive styles
  const labelSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const inputPadding = responsiveSize 
    ? `p-[${responsiveSize.padding}]`
    : getResponsiveValue({
        mobile: 'px-3 py-3',
        desktop: 'px-4 py-3'
      });

  const inputFontSize = responsiveSize
    ? `text-[${responsiveSize.fontSize}]`
    : getResponsiveValue({
        mobile: 'text-base', // 16px to prevent zoom on iOS
        desktop: 'text-sm'
      });

  const inputHeight = responsiveSize
    ? `h-[${responsiveSize.height}]`
    : getResponsiveValue({
        mobile: 'min-h-[44px]', // Touch-friendly minimum
        desktop: 'h-auto'
      });

  const iconPadding = getResponsiveValue({
    mobile: 'pl-9',
    desktop: 'pl-10'
  });

  const iconSize = getResponsiveValue({
    mobile: 'w-4 h-4',
    desktop: 'w-5 h-5'
  });

  const errorIconSize = getResponsiveValue({
    mobile: 'w-4 h-4',
    desktop: 'w-4 h-4'
  });

  const errorTextSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  return (
    <div className="flex flex-col space-y-2">
      {label && (
        <label className={`${labelSize} font-medium text-text-primary`}>
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {icon && (
          <div className={`absolute inset-y-0 left-0 ${getResponsiveValue({ mobile: 'pl-2.5', desktop: 'pl-3' })} flex items-center pointer-events-none`}>
            <div className={`text-text-tertiary ${iconSize}`}>
              {icon}
            </div>
          </div>
        )}
        
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full border border-border-primary rounded-xl bg-surface-primary
            focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-border-focus
            text-text-primary placeholder:text-text-tertiary
            disabled:bg-surface-secondary disabled:text-text-disabled disabled:cursor-not-allowed
            transition-colors duration-200
            ${inputPadding}
            ${inputFontSize}
            ${inputHeight}
            ${icon ? iconPadding : ''}
            ${error ? 'border-border-error focus:ring-border-error focus:border-border-error' : ''}
            ${isMobile ? 'rounded-xl' : 'rounded-xl'}
            ${className}
          `}
          {...props}
        />
      </div>
      
      {error && (
        <span className={`${errorTextSize} text-error flex items-center`}>
          <svg className={`${errorIconSize} mr-1 flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className={isMobile ? 'break-words' : ''}>{error}</span>
        </span>
      )}
    </div>
  );
};

export default Input;